import { httpClient } from "@/shared/api/http-client";
import { assertIdSegment } from "@/shared/lib/id-segment";

/**
 * Typed client of the organization access endpoints the backoffice writes
 * (v2.0 access model: add a member, change a member's role in place, assign
 * locations, reactivate a member, rename an organization). Every path and body shape lives in this file, so aligning with
 * the API is a one-file change. Hand-written (CLAUDE.md, TD-07).
 *
 * `locationIds` are venue ids for a VENUE organization and property ids for a
 * PROPERTY one: the organization decides the kind, the client never does.
 */

/** Roles an operator can grant: OWNER is never assigned or changed here. */
export type AssignableRole = "ADMIN" | "STAFF";

export interface AddOrganizationMemberInput {
  readonly organizationId: string;
  readonly userId: string;
  readonly role: AssignableRole;
  /** STAFF only (>= 1); ignored, and never sent, for ADMIN. */
  readonly locationIds?: readonly string[];
}

export interface ChangeMemberRoleInput {
  readonly organizationId: string;
  readonly membershipId: string;
  /** The new role: STAFF <-> ADMIN, in place on the same membership. */
  readonly role: AssignableRole;
  /** STAFF only (>= 1); ignored, and never sent, for ADMIN. */
  readonly locationIds?: readonly string[];
}

export interface AssignMemberLocationsInput {
  readonly organizationId: string;
  readonly membershipId: string;
  /** Location ids to assign (or re-activate), at most 100. */
  readonly add?: readonly string[];
  /** Location ids to deactivate, at most 100. An id cannot be in both lists. */
  readonly remove?: readonly string[];
}

/** What the API did with one requested location (`unchanged` / `not_assigned` = nothing to do). */
export type AssignmentOutcome =
  "created" | "reactivated" | "unchanged" | "deactivated" | "not_assigned";

export interface AssignmentResultRow {
  readonly kind: "VENUE" | "PROPERTY";
  readonly entityId: string;
  readonly action: "add" | "remove";
  readonly outcome: AssignmentOutcome;
}

export interface AssignMemberLocationsResult {
  readonly membershipId: string;
  readonly results: readonly AssignmentResultRow[];
}

export interface ReactivateMemberInput {
  readonly organizationId: string;
  readonly membershipId: string;
}

export interface ReactivateMemberResult {
  readonly membershipId: string;
  readonly organizationId: string;
  readonly userId: string;
  readonly role: AssignableRole;
  readonly status: "ACTIVE";
  /** `false`: it was already ACTIVE (idempotent repeat), nothing was written. */
  readonly changed: boolean;
}

export interface RenameOrganizationInput {
  readonly organizationId: string;
  readonly name: string;
}

const JSON_HEADERS = { "content-type": "application/json" } as const;

/**
 * Ids reach these paths from API payloads (a picked organization, a membership
 * row): an unsafe one (`..`, a slash) throws instead of re-routing the write.
 */
function organizationPath(organizationId: string): string {
  return `/api/admin/organizations/${assertIdSegment(organizationId, "organization id")}`;
}

function roleBody(role: AssignableRole, locationIds: readonly string[] | undefined) {
  return role === "STAFF" ? { role, locationIds: locationIds ?? [] } : { role };
}

/** `POST /admin/organizations/:organizationId/members` — assigns directly, no invitation. */
export async function addOrganizationMember(input: AddOrganizationMemberInput): Promise<unknown> {
  return httpClient<unknown>(`${organizationPath(input.organizationId)}/members`, {
    method: "POST",
    headers: JSON_HEADERS,
    body: JSON.stringify({ userId: input.userId, ...roleBody(input.role, input.locationIds) }),
  });
}

/** `PATCH /admin/organizations/:organizationId/members/:membershipId/role`. */
export async function changeMemberRole(input: ChangeMemberRoleInput): Promise<unknown> {
  const membership = assertIdSegment(input.membershipId, "membership id");
  return httpClient<unknown>(
    `${organizationPath(input.organizationId)}/members/${membership}/role`,
    {
      method: "PATCH",
      headers: JSON_HEADERS,
      body: JSON.stringify(roleBody(input.role, input.locationIds)),
    },
  );
}

/**
 * `POST /admin/organizations/:organizationId/members/:membershipId/assignments`
 * — add and remove locations of an ACTIVE STAFF member, all or nothing.
 */
export async function assignMemberLocations(
  input: AssignMemberLocationsInput,
): Promise<AssignMemberLocationsResult> {
  const membership = assertIdSegment(input.membershipId, "membership id");
  const body = {
    ...(input.add?.length ? { add: input.add } : {}),
    ...(input.remove?.length ? { remove: input.remove } : {}),
  };
  return httpClient<AssignMemberLocationsResult>(
    `${organizationPath(input.organizationId)}/members/${membership}/assignments`,
    { method: "POST", headers: JSON_HEADERS, body: JSON.stringify(body) },
  );
}

/**
 * `POST /admin/organizations/:organizationId/members/:membershipId/reactivate`
 * — INACTIVE -> ACTIVE for an ADMIN or STAFF member. Locations are not restored.
 */
export async function reactivateMember(
  input: ReactivateMemberInput,
): Promise<ReactivateMemberResult> {
  const membership = assertIdSegment(input.membershipId, "membership id");
  return httpClient<ReactivateMemberResult>(
    `${organizationPath(input.organizationId)}/members/${membership}/reactivate`,
    { method: "POST" },
  );
}

/** `PATCH /admin/organizations/:organizationId/name` — the picker's short name. */
export async function renameOrganization(input: RenameOrganizationInput): Promise<unknown> {
  return httpClient<unknown>(`${organizationPath(input.organizationId)}/name`, {
    method: "PATCH",
    headers: JSON_HEADERS,
    body: JSON.stringify({ name: input.name.trim() }),
  });
}
